import * as React from "react";
import { HexColorPicker } from "react-colorful";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import {
  Check,
  Upload,
  Trash2,
  Palette,
  Image as ImageIcon,
  Minus,
  Plus,
  Sparkles,
  Droplet,
  Ban,
  ChevronDown,
  ChevronUp,
  Dices,
} from "lucide-react";
import { ControlOption } from "./ControlOption";
import { usePaginatedCombobox } from "@/utils";
import { Combobox } from "@/components/ui";
import { ContentPageThemeServices } from "@/services/bedrock";

const PRESET_GRADIENTS = [
  // Curated, universally flattering portrait-complementary studio gradients
  { label: "Studio Gray", value: ["#3E4044", "#1A1B1C"] },
  { label: "Executive Blue", value: ["#1E2A3A", "#0F172A"] },
  { label: "Deep Teal", value: ["#132E35", "#07171C"] },
  { label: "Nordic Slate", value: ["#2D3A3A", "#151D1D"] },
  { label: "Warm Slate", value: ["#2F2F31", "#1B1B1C"] },
  { label: "Midnight Indigo", value: ["#1C1625", "#0E0B13"] },
  { label: "Warm Sand", value: ["#E2D4C9", "#C5B4A5"] },
  { label: "Champagne Gold", value: ["#DFCBB5", "#B8A390", "#7F6B58"] },
  { label: "Studio Light", value: ["#FFFFFF", "#E2E8F0"] },
  { label: "Morning Blush", value: ["#FCE7F3", "#FEE2E2", "#FEF3C7"] },
];

const PRESET_IMAGES = [
  {
    label: "Boardroom",
    horizontal: "https://cdn.socialcoach.io/backgrounds/boardroom-horizontal.jpeg",
    vertical: "https://cdn.socialcoach.io/backgrounds/boardroom-vertical.jpeg",
  },
  {
    label: "Modern Office",
    horizontal: "https://cdn.socialcoach.io/backgrounds/modern-office-horizontal.jpeg",
    vertical: "https://cdn.socialcoach.io/backgrounds/modern-office-vertical.jpeg",
  },
  {
    label: "Open Office",
    horizontal: "https://cdn.socialcoach.io/backgrounds/open-office-horizontal.jpeg",
    vertical: "https://cdn.socialcoach.io/backgrounds/open-office-vertical.jpeg",
  },
  {
    label: "Home Office",
    horizontal: "https://cdn.socialcoach.io/backgrounds/home-office-horizontal.jpeg",
    vertical: "https://cdn.socialcoach.io/backgrounds/home-office-vertical.jpeg",
  },
  {
    label: "Library",
    horizontal: "https://cdn.socialcoach.io/backgrounds/library-horizontal.jpeg",
    vertical: "https://cdn.socialcoach.io/backgrounds/library-vertical.jpeg",
  },
  {
    label: "Golf Course",
    horizontal: "https://cdn.socialcoach.io/backgrounds/golf-horizontal.jpeg",
    vertical: "https://cdn.socialcoach.io/backgrounds/golf-vertical.jpeg",
  },
  {
    label: "Mountain",
    horizontal: "https://cdn.socialcoach.io/backgrounds/mountain-horizontal.jpeg",
    vertical: "https://cdn.socialcoach.io/backgrounds/mountain-vertical.jpeg",
  },
];

const getBlurLabel = (blur: number) => {
  if (blur <= 4) return "Subtle";
  if (blur <= 10) return "Normal";
  if (blur <= 18) return "High";
  return "Intense";
};

interface VirtualBackgroundControlProps {
  aspectRatio: number;
  bgType: "none" | "blur" | "gradient" | "image";
  bgBlur: number;
  bgColors: string[];
  bgImage: string | null;
  bgLogo: string | null;
  onBgLogoChange: (logo: string | null) => void;
  bgLogoStyle: "none" | "watermark" | "backdrop" | "repeat";
  onBgLogoStyleChange: (style: "none" | "watermark" | "backdrop" | "repeat") => void;
  onBgTypeChange: (type: "none" | "blur" | "gradient" | "image") => void;
  onBgBlurChange: (blur: number) => void;
  onBgColorsChange: (colors: string[]) => void;
  onBgImageChange: (image: string | null) => void;
}

export const VirtualBackgroundControl: React.FC<VirtualBackgroundControlProps> = ({
  aspectRatio,
  bgType,
  bgBlur,
  bgColors,
  bgImage,
  bgLogo,
  onBgLogoChange,
  bgLogoStyle,
  onBgLogoStyleChange,
  onBgTypeChange,
  onBgBlurChange,
  onBgColorsChange,
  onBgImageChange,
}) => {
  const [isExpanded, setIsExpanded] = React.useState<boolean>(() => {
    const storedBgType = localStorage.getItem("scRecorderBgType");
    if (storedBgType === "gradient" || storedBgType === "image") {
      return false;
    }
    return true;
  });

  const [activeColorIdx, setActiveColorIdx] = React.useState<number>(0);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const fetchPageThemes = React.useCallback((search: string | undefined, page: number) => {
    return ContentPageThemeServices.getPageThemes({ page, perPage: 25, search });
  }, []);

  const pageThemesCombobox = usePaginatedCombobox(fetchPageThemes, false, null);

  const bgTabs = [
    { label: "None", value: "none", icon: Ban },
    { label: "Blur", value: "blur", icon: Droplet },
    { label: "Color", value: "gradient", icon: Palette },
    { label: "Photo", value: "image", icon: ImageIcon },
  ];

  React.useEffect(() => {
    if (!bgImage) return;

    const currentPreset = PRESET_IMAGES.find(preset => preset.horizontal === bgImage || preset.vertical === bgImage);

    if (currentPreset) {
      const targetUrl = aspectRatio === 9 / 16 ? currentPreset.vertical : currentPreset.horizontal;
      if (bgImage !== targetUrl) {
        onBgImageChange(targetUrl);
      }
    }
  }, [aspectRatio, bgImage, onBgImageChange]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onBgImageChange(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <ControlOption title="Virtual background">
      <div className="relative w-full pt-1">
        {/* Expand / Collapse Toggle for Color and Photo */}
        {(bgType === "gradient" || bgType === "image") && (
          <div className="absolute -top-7 right-1 z-20">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs font-semibold text-teal-600 hover:text-teal-700 hover:underline flex items-center gap-1 cursor-pointer select-none leading-none bg-transparent border-0"
            >
              {isExpanded ? (
                <>
                  <span>Hide settings</span>
                  <ChevronUp className="size-3.5" />
                </>
              ) : (
                <>
                  <span>Customize background</span>
                  <ChevronDown className="size-3.5" />
                </>
              )}
            </button>
          </div>
        )}

        {/* Segmented Top-Level control */}
        <div className="flex bg-gray-100 p-1 rounded-lg w-full mb-3 border border-gray-200/50 mt-1.5">
          {bgTabs.map(tab => {
            const isActive = bgType === tab.value;
            const IconComp = tab.icon;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => {
                  onBgTypeChange(tab.value as any);
                  setIsExpanded(true);
                }}
                className={`flex-1 flex flex-col items-center justify-center py-1.5 rounded-md text-[10px] transition-all cursor-pointer font-semibold ${
                  isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <IconComp className="size-4 mb-0.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Sub-options rendering seamlessly */}
        {bgType === "blur" && (
          <div className="flex flex-col gap-2 w-full pt-1">
            <div className="flex justify-between items-center select-none">
              <span className="text-xs font-semibold text-gray-500">Blur strength</span>
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md">
                {getBlurLabel(bgBlur)}
              </span>
            </div>
            <div className="pt-2 px-1">
              <Slider
                min={2}
                max={25}
                step={1}
                value={[bgBlur]}
                onValueChange={val => {
                  const newVal = Array.isArray(val) ? val[0] : val;
                  onBgBlurChange(newVal);
                }}
              />
            </div>
          </div>
        )}

        {bgType === "gradient" &&
          isExpanded &&
          (() => {
            const handleRandomizeColors = () => {
              const generateVibrantHex = () => {
                const h = Math.floor(Math.random() * 360);
                const s = 80 + Math.floor(Math.random() * 20); // 80-100% saturation
                const l = 50 + Math.floor(Math.random() * 15); // 50-65% lightness

                const hDecimal = h / 360;
                const sDecimal = s / 100;
                const lDecimal = l / 100;
                let r = lDecimal;
                let g = lDecimal;
                let b = lDecimal;
                if (sDecimal !== 0) {
                  const q = lDecimal < 0.5 ? lDecimal * (1 + sDecimal) : lDecimal + sDecimal - lDecimal * sDecimal;
                  const p = 2 * lDecimal - q;
                  const hue2rgb = (t: number) => {
                    if (t < 0) t += 1;
                    if (t > 1) t -= 1;
                    if (t < 1 / 6) return p + (q - p) * 6 * t;
                    if (t < 1 / 2) return q;
                    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                    return p;
                  };
                  r = hue2rgb(hDecimal + 1 / 3);
                  g = hue2rgb(hDecimal);
                  b = hue2rgb(hDecimal - 1 / 3);
                }
                const toHex = (x: number) => {
                  const hex = Math.round(x * 255).toString(16);
                  return hex.length === 1 ? "0" + hex : hex;
                };
                return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
              };

              const newColors = bgColors.map(() => generateVibrantHex());
              onBgColorsChange(newColors);
            };

            const handleAddColor = () => {
              if (bgColors.length >= 5) return;
              const lastColor = bgColors[bgColors.length - 1];
              const newColors = [...bgColors, lastColor];
              onBgColorsChange(newColors);
              setActiveColorIdx(newColors.length - 1);
            };

            const handleRemoveColor = (idx: number) => {
              if (bgColors.length <= 1) return;
              const newColors = bgColors.filter((_, i) => i !== idx);
              onBgColorsChange(newColors);
              setActiveColorIdx(Math.max(0, idx - 1));
            };

            const handleColorPickerChange = (newColor: string) => {
              const newColors = [...bgColors];
              newColors[activeColorIdx] = newColor;
              onBgColorsChange(newColors);
            };

            const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              let val = e.target.value.trim();
              if (val && !val.startsWith("#")) {
                val = "#" + val;
              }
              const newColors = [...bgColors];
              newColors[activeColorIdx] = val;
              onBgColorsChange(newColors);
            };

            return (
              <div className="flex flex-col gap-3.5 w-full pt-1.5">
                {/* Page Theme Combobox Dropdown */}
                <div className="flex flex-col gap-1.5 w-full pb-2.5 select-none text-left">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Apply Theme</span>
                  <Combobox
                    {...pageThemesCombobox}
                    itemToStringLabel={item => item.name}
                    placeholder="Select page theme..."
                    showClear
                    className="w-full text-sm"
                    onValueChange={(theme, details) => {
                      pageThemesCombobox.onValueChange(theme, details);
                      if (theme) {
                        onBgColorsChange([theme.backgroundColor, theme.primaryCtaColor || theme.backgroundColor]);
                        setActiveColorIdx(0);
                        const logoUrl = theme.headerMedia?.[0]?.uri || null;
                        onBgLogoChange(logoUrl);
                        if (logoUrl && bgLogoStyle === "none") {
                          onBgLogoStyleChange("watermark");
                        }
                      } else {
                        onBgLogoChange(null);
                        onBgLogoStyleChange("none");
                      }
                    }}
                  />
                </div>

                {/* Logo Style Toggles (Only show if a theme has a logo) */}
                {bgLogo && (
                  <div className="flex flex-col gap-1.5 w-full pb-2.5 border-b border-gray-150 select-none text-left animate-in fade-in duration-200">
                    <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                      Theme Logo Placement
                    </span>
                    <div className="flex bg-gray-100 p-1 rounded-lg w-full border border-gray-200/50 mt-0.5 max-w-[260px]">
                      {[
                        { label: "Off", value: "none" },
                        { label: "Watermark", value: "watermark" },
                        { label: "Repeat", value: "repeat" },
                      ].map(style => {
                        const isActive = bgLogoStyle === style.value;
                        return (
                          <button
                            key={style.value}
                            type="button"
                            onClick={() => onBgLogoStyleChange(style.value as any)}
                            className={`flex-1 py-1 rounded-md text-[10px] transition-all cursor-pointer font-bold ${
                              isActive ? "bg-white text-gray-900 shadow-xs" : "text-gray-500 hover:text-gray-900"
                            }`}
                          >
                            {style.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Presets Grid */}
                <div className="grid grid-cols-5 gap-x-3.5 gap-y-2.5 items-center px-1.5 py-1">
                  {PRESET_GRADIENTS.map(preset => {
                    const isSelected =
                      bgColors.length === preset.value.length && bgColors.every((c, i) => c === preset.value[i]);

                    let backgroundStyle = "";
                    if (preset.value.length === 1) {
                      backgroundStyle = preset.value[0];
                    } else {
                      backgroundStyle = `radial-gradient(circle at 50% 35%, ${preset.value.join(", ")})`;
                    }

                    return (
                      <button
                        key={preset.label}
                        type="button"
                        className={`w-9 h-9 rounded-lg cursor-pointer hover:scale-105 active:scale-95 transition-all flex items-center justify-center border relative group ${
                          isSelected ? "ring-2 ring-primary ring-offset-2 border-white" : "border-gray-200 shadow-3xs"
                        }`}
                        style={{ background: backgroundStyle }}
                        onClick={() => {
                          onBgColorsChange(preset.value);
                          setActiveColorIdx(0);
                        }}
                        title={preset.label}
                      >
                        {isSelected && <Check className="size-4 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]" />}
                      </button>
                    );
                  })}
                </div>

                {/* Custom Gradient Builder Section */}
                <div className="mt-2.5 pt-2.5 border-t border-gray-150 flex flex-col gap-3 select-none">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Custom Canvas</span>

                    {/* Randomize & Add Color Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleRandomizeColors}
                        className="flex items-center gap-1.5 text-xs font-bold text-teal-600 hover:text-teal-700 bg-teal-50 hover:bg-teal-100 px-2.5 py-1.5 rounded-md border border-teal-200/50 cursor-pointer active:scale-95 transition-all shadow-3xs leading-none"
                        title="Randomize Colors"
                      >
                        <Dices className="size-3.5" />
                        <span>Randomize</span>
                      </button>

                      {bgColors.length < 5 && (
                        <button
                          type="button"
                          onClick={handleAddColor}
                          className="flex items-center gap-1.5 text-xs font-bold text-primary hover:text-primary bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-md border border-blue-200/50 cursor-pointer active:scale-95 transition-all shadow-3xs leading-none"
                          title="Add Color Stop"
                        >
                          <Plus className="size-3.5" />
                          <span>Add Color</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Colors Stops Row */}
                  <div className="flex items-center gap-3.5 px-1.5 py-1">
                    {bgColors.map((color, idx) => {
                      const isEditing = idx === activeColorIdx;
                      return (
                        <div key={idx} className="relative group">
                          <button
                            type="button"
                            onClick={() => setActiveColorIdx(idx)}
                            className={`w-9 h-9 rounded-full cursor-pointer hover:scale-105 active:scale-95 transition-all border ${
                              isEditing
                                ? "ring-2 ring-primary ring-offset-2 border-white"
                                : "border-gray-200 shadow-3xs"
                            }`}
                            style={{ backgroundColor: color }}
                            title={`Color stop ${idx + 1}`}
                          />

                          {bgColors.length > 1 && (
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                handleRemoveColor(idx);
                              }}
                              className="absolute -top-1 -right-1 bg-red-100 hover:bg-red-200 text-red-600 p-0.5 rounded-full shadow-3xs cursor-pointer hover:scale-110 active:scale-90 transition-all opacity-0 group-hover:opacity-100"
                              title="Remove color stop"
                            >
                              <Trash2 className="size-2.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Dynamic Color Picker */}
                  <div className="mt-1 flex flex-col justify-center bg-background p-2 rounded-lg border border-gray-200 gap-1.5 w-full">
                    <div className="flex justify-between items-center px-1">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        Editing Stop #{activeColorIdx + 1}
                      </span>
                      <input
                        type="text"
                        value={bgColors[activeColorIdx] || "#FFFFFF"}
                        onChange={handleHexInputChange}
                        maxLength={7}
                        className="text-xs font-mono font-semibold text-center text-gray-700 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded-md w-18 focus:outline-none focus:ring-1 focus:ring-primary focus:bg-white select-text cursor-text"
                      />
                    </div>
                    <HexColorPicker
                      color={bgColors[activeColorIdx] || "#FFFFFF"}
                      onChange={handleColorPickerChange}
                      style={{ width: "100%", height: "105px" }}
                    />
                  </div>
                </div>
              </div>
            );
          })()}

        {bgType === "image" && isExpanded && (
          <div className="flex flex-col gap-3 w-full pt-2">
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {/* Presets Grid */}
            <div className="grid grid-cols-4 gap-2">
              {PRESET_IMAGES.map(preset => {
                const imageUrl = aspectRatio === 9 / 16 ? preset.vertical : preset.horizontal;
                const isSelected = bgImage === imageUrl;
                return (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => onBgImageChange(imageUrl)}
                    className={`relative rounded-lg overflow-hidden border cursor-pointer hover:scale-[1.03] active:scale-[0.98] transition-all flex items-center justify-center bg-gray-50 group ${
                      aspectRatio === 9 / 16 ? "aspect-[9/16]" : "aspect-video"
                    } ${isSelected ? "ring-2 ring-teal-500 ring-offset-2 border-white" : "border-gray-200 shadow-3xs"}`}
                    title={preset.label}
                  >
                    <img src={imageUrl} className="absolute inset-0 w-full h-full object-cover" alt={preset.label} />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                    <span className="absolute bottom-1 left-1 right-1 text-[8px] font-bold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] truncate leading-none">
                      {preset.label}
                    </span>
                    {isSelected && (
                      <div className="absolute top-1 right-1 bg-teal-500 text-white p-0.5 rounded-full shadow-xs">
                        <Check className="size-2" />
                      </div>
                    )}
                  </button>
                );
              })}

              {/* Upload Button Grid Item */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`relative rounded-lg border-2 border-dashed border-gray-300/80 cursor-pointer hover:bg-gray-50 hover:border-gray-400 active:scale-[0.98] transition-all flex flex-col items-center justify-center text-gray-500 hover:text-gray-700 bg-white gap-1 select-none ${
                  aspectRatio === 9 / 16 ? "aspect-[9/16]" : "aspect-video"
                }`}
                title="Upload Custom Photo"
              >
                <Upload className="size-4.5" />
                <span className="text-xs font-bold tracking-tight leading-none">Upload</span>
              </button>
            </div>

            {bgImage && (
              <div className="flex items-center gap-4 bg-background p-2.5 rounded-lg border border-gray-200 shadow-3xs mt-1">
                <img
                  src={bgImage}
                  className="w-16 h-16 object-cover rounded-md border border-gray-100 shrink-0"
                  alt="Background Preview"
                />
                <div className="flex flex-col gap-1.5 flex-1 min-w-0 text-left">
                  <span className="text-xs font-semibold text-gray-500 truncate select-none leading-none">
                    Photo Backdrop Active
                  </span>
                  <div className="flex gap-2 items-center">
                    <Button
                      size="xs"
                      variant="outline"
                      className="h-7 px-2.5 text-[11px] font-semibold cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Replace
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      className="h-7 p-0 text-[11px] font-normal text-gray-500 hover:text-red-600 hover:bg-transparent cursor-pointer"
                      onClick={() => onBgImageChange(null)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </ControlOption>
  );
};
